import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VENDOR_ADVANCE_MODES, type Vendor, type VendorAdvanceEntry } from '@fpc/shared';
import { api } from '@/lib/api';
import { formatDate, humanize, rupeesToMinor } from '@/lib/format';
import { useAuth } from '@/hooks/useAuth';
import { useLayout } from '@/hooks/useMediaQuery';
import { ResponsiveRecords } from './ResponsiveRecords';
import { ConfirmWithReason, ErrorState, Modal, Money, Spinner, Table, ToneBadge } from './ui';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * A vendor's advance balance: what is left, every movement that explains it,
 * and — for whoever may — a top-up form and per-entry reversal.
 *
 * Money recorded here was paid outside the platform; the accounting team
 * draws on the balance when it verifies an invoice.
 */
export function VendorAdvancePanel({ vendor, onClose }: { vendor: Vendor; onClose(): void }) {
  const { can } = useAuth();
  const layout = useLayout();
  const queryClient = useQueryClient();
  const [reversing, setReversing] = useState<VendorAdvanceEntry | null>(null);

  const ledger = useQuery({
    queryKey: ['vendor-advance', vendor.id],
    queryFn: () => api.settings.vendorAdvance(vendor.id),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['vendor-advance', vendor.id] });
    void queryClient.invalidateQueries({ queryKey: ['settings-vendors'] });
  };

  const reverse = useMutation({
    mutationFn: ({ entry, reason }: { entry: VendorAdvanceEntry; reason: string }) =>
      api.settings.reverseVendorAdvance(vendor.id, entry.id, { reason }),
    onSuccess: () => {
      setReversing(null);
      refresh();
    },
  });

  const entries = ledger.data?.entries ?? [];
  const currency = ledger.data?.currency;
  // A deposit or top-up that has not been undone; adjustments go back through their invoice.
  const reversible = (entry: VendorAdvanceEntry) =>
    can('vendor_advance:reverse') &&
    (entry.type === 'DEPOSIT' || entry.type === 'TOP_UP') &&
    !entry.reversedByEntryId;

  const signed = (entry: VendorAdvanceEntry) => (
    <span className={entry.direction === 'CREDIT' ? 'text-emerald-700' : 'text-ink-900'}>
      {entry.direction === 'CREDIT' ? '+' : '−'}
      <Money minor={entry.amount} currency={currency} />
    </span>
  );
  const what = (entry: VendorAdvanceEntry) =>
    entry.invoiceTrackingId
      ? `${entry.invoiceTrackingId}${entry.note ? ` · ${entry.note}` : ''}`
      : [entry.mode, entry.reference, entry.note].filter(Boolean).join(' · ') || '—';
  const reverseButton = (entry: VendorAdvanceEntry) =>
    reversible(entry) ? (
      <button className="text-sm text-red-700" onClick={() => setReversing(entry)}>
        Reverse
      </button>
    ) : null;

  return (
    <Modal title={`Advance — ${vendor.name}`} onClose={onClose} wide>
      {ledger.isLoading ? (
        <Spinner label="Loading the advance ledger" />
      ) : ledger.error ? (
        <ErrorState error={ledger.error} />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-500">Available balance</p>
              <p className="text-2xl font-semibold text-ink-900">
                <Money minor={ledger.data?.balance ?? 0} currency={currency} />
              </p>
            </div>
            <p className="max-w-md text-sm text-ink-500">
              Paid to the vendor in advance. The accounting team uses it to reduce what an invoice
              still owes when it verifies the invoice.
            </p>
          </div>

          {can('vendor_advance:create') ? (
            <TopUpForm vendorId={vendor.id} first={entries.length === 0} onSaved={refresh} />
          ) : null}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-900">Ledger</h3>
            {entries.length === 0 ? (
              <p className="text-sm text-ink-500">No advance recorded yet.</p>
            ) : (
              <div className="-mx-5 border-t border-ink-100">
                <ResponsiveRecords
                  layout={layout}
                  records={entries.map((entry) => ({
                    key: entry.id,
                    title: humanize(entry.type),
                    subtitle: entry.entryNo,
                    heading: (
                      <span className="flex justify-between gap-3">
                        <span>{humanize(entry.type)}</span>
                        {signed(entry)}
                      </span>
                    ),
                    badge: entry.reversedByEntryId ? (
                      <ToneBadge tone="neutral">Reversed</ToneBadge>
                    ) : undefined,
                    details: [
                      { label: 'Amount', value: signed(entry) },
                      {
                        label: 'Balance after',
                        value: <Money minor={entry.balanceAfter} currency={currency} />,
                      },
                      { label: 'Date', value: formatDate(entry.paidOn ?? entry.createdAt) },
                      { label: 'Details', value: what(entry) },
                      { label: 'By', value: entry.createdByName ?? '—' },
                    ],
                    actions: reverseButton(entry) ?? undefined,
                  }))}
                  table={
                    <Table>
                      <thead className="thead">
                        <tr>
                          <th className="th">Entry</th>
                          <th className="th">Date</th>
                          <th className="th">Details</th>
                          <th className="th text-right">Amount</th>
                          <th className="th text-right">Balance</th>
                          <th className="th" aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody className="tbody">
                        {entries.map((entry) => (
                          <tr key={entry.id}>
                            <td className="td">
                              <span className="block">
                                {humanize(entry.type)}
                                {entry.reversedByEntryId ? (
                                  <span className="ml-2">
                                    <ToneBadge tone="neutral">Reversed</ToneBadge>
                                  </span>
                                ) : null}
                              </span>
                              <span className="font-mono text-xs text-ink-500">
                                {entry.entryNo}
                              </span>
                            </td>
                            <td className="td whitespace-nowrap">
                              {formatDate(entry.paidOn ?? entry.createdAt)}
                            </td>
                            <td className="td min-w-[12rem] whitespace-normal text-sm text-ink-600">
                              {what(entry)}
                            </td>
                            <td className="td text-right">{signed(entry)}</td>
                            <td className="td text-right">
                              <Money minor={entry.balanceAfter} currency={currency} />
                            </td>
                            <td className="td text-right">{reverseButton(entry)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  }
                />
              </div>
            )}
          </section>
        </div>
      )}

      {reversing ? (
        <ConfirmWithReason
          title={`Reverse ${reversing.entryNo}`}
          description="Takes this amount back off the vendor's advance balance. The entry stays in the ledger, marked reversed, alongside the reversal."
          actionLabel="Reverse entry"
          onClose={() => setReversing(null)}
          onConfirm={(reason) => reverse.mutate({ entry: reversing, reason })}
          pending={reverse.isPending}
          error={reverse.error}
        />
      ) : null}
    </Modal>
  );
}

/** Records a deposit or top-up paid outside the platform. */
function TopUpForm({
  vendorId,
  first,
  onSaved,
}: {
  vendorId: string;
  first: boolean;
  onSaved(): void;
}) {
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(today);
  const [mode, setMode] = useState<string>('NEFT');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (minor: number) =>
      api.settings.addVendorAdvance(vendorId, {
        type: first ? 'DEPOSIT' : 'TOP_UP',
        amount: minor,
        paidOn,
        mode,
        reference: reference.trim(),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setAmount('');
      setReference('');
      setNote('');
      onSaved();
    },
  });

  const submit = () => {
    const minor = rupeesToMinor(amount);
    if (!minor) return setProblem('Enter the amount paid, e.g. 50,000');
    if (!reference.trim()) return setProblem('Enter the bank or cheque reference');
    setProblem(null);
    save.mutate(minor);
  };

  return (
    <section className="rounded-xl border border-ink-100 bg-ink-50/50 p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink-900">
        {first ? 'Record an advance deposit' : 'Top up the advance'}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="label">
            Amount<span className="text-red-600"> *</span>
          </span>
          <input
            className="input"
            inputMode="decimal"
            required
            aria-required="true"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="50,000"
          />
        </label>
        <label className="block">
          <span className="label">Paid on</span>
          <input
            className="input"
            type="date"
            value={paidOn}
            max={today()}
            onChange={(event) => setPaidOn(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Mode</span>
          <select className="input" value={mode} onChange={(event) => setMode(event.target.value)}>
            {VENDOR_ADVANCE_MODES.map((option) => (
              <option key={option} value={option}>
                {humanize(option)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">UTR / cheque no.</span>
          <input
            className="input"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </label>
        <label className="block sm:col-span-2 lg:col-span-3">
          <span className="label">Note (optional)</span>
          <input className="input" value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
        <div className="flex items-end">
          <button className="btn-primary w-full" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'Saving…' : first ? 'Record deposit' : 'Add top-up'}
          </button>
        </div>
      </div>
      {problem ? <p className="mt-2 text-sm text-red-700">{problem}</p> : null}
      {save.error ? (
        <div className="mt-3">
          <ErrorState error={save.error} compact />
        </div>
      ) : null}
    </section>
  );
}
